import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { map, switchMap } from 'rxjs';

import { GridSearchEntityInstance } from '@models/module/grid';
import { DataCard } from '@models/module/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import { Currency, CurrencyFn } from '@models/system';
import { DataCardViewComponent } from '@module-instance/data-card/data-card-view/data-card-view.component';
import { DataCardsViewService } from '@module-instance/data-card/data-cards-view/data-cards-view.service';
import { CipoTableOptions, TablePaginator } from '@shared/components/data-list/common';
import { PAGE_SIZE_OPTIONS } from '@shared/consts';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NotFoundComponent } from '@system/not-found/not-found.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  selector: 'app-data-cards-view',
  templateUrl: './data-cards-view.component.html',
  styleUrls: ['./data-cards-view.component.scss'],
  imports: [
    DataCardViewComponent,
    MatPaginatorModule,
    FlexLayoutModule,
    NotFoundComponent,
    TranslateModule,
    NgxSkeletonLoaderModule,
  ],
})
export class DataCardsViewComponent {
  private readonly cardsService = inject(DataCardsViewService);

  screenType = input.required<number>({ alias: 'screentype' });
  actions = input.required<DataListViewAction[]>();
  rows = input<GridSearchEntityInstance[]>([]);
  currency = input<Currency | CurrencyFn>();
  options = model<CipoTableOptions>({});
  isLoading = input.required<boolean>();

  cardClick = output<GridSearchEntityInstance>({ alias: 'cardclick' });
  /* TODO: Check this usage after cleanup Contracts from AngularJS */
  paginationUpdated = output<TablePaginator>();

  private readonly fields$ = toObservable(this.screenType).pipe(
    switchMap(type => this.cardsService.getScreenFields(type)),
    map(f => f.data || []),
  );

  fields = toSignal(this.fields$, { initialValue: [] });

  cards = computed(() => {
    if (!this.rows()?.length || !this.fields()?.length) return [];
    return this.rows().map(row => this.cardsService.getDataCard(this.fields(), row, this.currency()));
  });

  serverPaginate = computed<TablePaginator | null>(() => {
    const opt = this.options();

    if (!opt?.serverPaginationSort || !opt?.pagination) return null;

    const { length, pageIndex, pageSize, pageSizeOptions } = opt.pagination;
    const size = pageSize ?? PAGE_SIZE_OPTIONS.default[1];
    const total = length ?? PAGE_SIZE_OPTIONS.default[1];

    return {
      length: total,
      pageIndex: pageIndex ?? 0,
      pageSize: size,
      pageSizeOptions: pageSizeOptions ?? PAGE_SIZE_OPTIONS.default,
      pagesCount: Math.ceil(total / size),
    };
  });

  internalLoading = signal(false);
  loading = computed(() => this.isLoading() || (this.fields().length === 0 && this.rows().length > 0));

  skeletonItems = computed(() => {
    const size = this.options()?.pagination?.pageSize ?? 6;
    return Array.from({ length: size }).map((_, i) => i);
  });

  cardClickInternal(card: DataCard) {
    const row = this.rows().find(r => r.entity_instance_id === card.entity_instance_id);
    if (row) {
      this.cardClick.emit(row);
    }
  }

  paginationChange(event: PageEvent) {
    this.options.update(options => {
      if (!options?.pagination) {
        return options;
      }
      return {
        ...options,
        pagination: {
          ...options.pagination,
          pageIndex: event.pageIndex,
          pageSize: event.pageSize,
        },
      };
    });
    this.paginationUpdated.emit({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      length: event.length,
    });
  }
}
